import { skipToken, useSuspenseQuery } from '@apollo/client';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.js';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.js';
import { Skeleton } from '@synoptic/ui-kit/skeleton/skeleton.js';
import { clsx } from 'clsx';
import { memo, Suspense } from 'react';
import { gql } from '@/__generated__/gql.ts';
import {
  BaseStreamPostCardFragmentFragment,
  BaseTournamentPostCardFragmentFragment,
  RegularPostPreviewFragmentFragment,
} from '@/__generated__/graphql.ts';
import { formatDateWithMs } from '@/date-utils/format-date-with-ms.ts';
import { formatDate } from '@/date-utils/format-date.ts';
import { TextEditor } from '../../text-editor/text-editor.tsx';
import { TournamentPostCardContent } from '../../tournament-post-card/tournament-post-card-content.tsx';
import { PostAuthor } from '../post-author.tsx';
import { PostCardMedia } from '../post-card-media/post-card-media.tsx';
import {
  postCardBody,
  postCardContainer,
  postCardContent,
  postCardDate,
  postCardDeletedPlaceholder,
  postCardHeader,
  postCardHeaderContent,
} from '../post-card.css.ts';
import {
  previewPostCardContainer,
  previewPostCardContainerWithHover,
} from './post-card-preview.css.ts';

type PostProps = {
  post:
    | RegularPostPreviewFragmentFragment
    | BaseStreamPostCardFragmentFragment
    | BaseTournamentPostCardFragmentFragment;
  clickableAuthor?: boolean;
  hoverable?: boolean;
};
export const PostCardPreview = memo<PostProps>(function PostCardPreview({
  post,
  clickableAuthor = true,
  hoverable = false,
}) {
  const isDeleted = Boolean(post.deletedAt);

  return (
    <div
      className={clsx(
        postCardContainer,
        previewPostCardContainer,
        hoverable && previewPostCardContainerWithHover,
      )}
    >
      <div className={postCardHeader}>
        <div className={postCardHeaderContent}>
          <Suspense
            fallback={
              <div style={{ height: 48 }}>
                <Skeleton height={15} width={150} />
                <br />
                <Skeleton height={10} width={100} />
              </div>
            }
          >
            <PostCardPreviewAuthor
              post={post}
              clickableAuthor={clickableAuthor}
            />
          </Suspense>
        </div>
      </div>

      <div className={postCardContent}>
        {isDeleted ? (
          <EmptyState
            illustration={<TrashIcon />}
            heading="The post was deleted"
            className={postCardDeletedPlaceholder}
          />
        ) : post.__typename === 'TournamentPost' ? (
          <TournamentPostCardContent post={post} />
        ) : (
          <>
            <TextEditor
              namespace={`post:${post.id}`}
              className={postCardBody}
              initialState={
                post.__typename === 'StreamPost'
                  ? post.text
                  : post.json || post.text
              }
              format={post.__typename === 'StreamPost' ? 'md' : 'json'}
              editable={false}
            />

            <PostCardMedia
              media={post.media}
              authorId={post.authorId}
              createdAt={post.createdAt}
            />
          </>
        )}

        <time
          title={formatDateWithMs(new Date(post.createdAt))}
          dateTime={post.createdAt}
          className={postCardDate}
        >
          {formatDate(new Date(post.createdAt))}
        </time>
      </div>
    </div>
  );
});

const PostCardPreviewAuthorQuery = gql(`
  query PostCardPreviewAuthorQuery($id: ID!) {
    userById(id: $id) {
      id
      ...UnauthenticatedPostCardAuthorFragment
    }
  }
`);

export const PostCardPreviewAuthor = memo<PostProps>(
  function PostCardPreviewAuthor({ post, clickableAuthor = true }) {
    const { data } = useSuspenseQuery(
      PostCardPreviewAuthorQuery,
      !post.author
        ? {
            variables: { id: post.authorId },
          }
        : //skip author query if author is already in the post
          skipToken,
    );

    return (
      <PostAuthor
        author={{ ...(post.author || data?.userById), following: false }}
        clickableAuthor={clickableAuthor}
      />
    );
  },
);
